import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LiliButton, LiliTabs } from '@lili/lili-frontend-common';
import PageHeader from '../../../generic-components/PageHeader/PageHeader';
import { UnpaidInvoices } from './components/UnpaidInvoices/UnpaidInvoices';
import { HistoryInvoices } from './components/HistoryInvoices/HistoryInvoices';
import { InvoiceActionsContext } from './contexts/InvoiceActionsContext';
import { InvoiceSettingsContext } from './contexts/InvoiceSettingsContext';
import { ReactComponent as PlusSVG } from '../../../assets/svg/invoices-icons/icon-plus.svg';
import { InvoiceHubModals } from './components/InvoiceHubModals';
import { useStore } from '../../../store';
import { InvoiceSettings } from './components/InvoiceSettings';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../constants/general';
import { FeatureAccess } from '../../AccountSettings/components/rolesAndPermissions/PermissionsList';

enum TAB_INDEX {
  UNPAID,
  HISTORY,
}

export const InvoiceHub = () => {
  const resetUnpaidTab = useStore((state) => state.resetUnpaidTab);
  const resetHistoryTab = useStore((state) => state.resetHistoryTab);
  const [activeTab, setActiveTab] = useState(TAB_INDEX.UNPAID);
  const invoicesPermission = featureAccess(FEATURE_FLAG.INVOICE, LimitedFeatureType.Invoices);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoiceActionsReferenceElement, setInvoiceActionsReferenceElement] = useState<any>();
  const [invoiceSettingsReferenceElement, setInvoiceSettingsReferenceElement] = useState<any>();
  const invoiceActionsContextValue = useMemo(
    () => ({
      setInvoiceActionsReferenceElement,
      invoiceActionsReferenceElement,
    }),
    [setInvoiceActionsReferenceElement, invoiceActionsReferenceElement],
  );
  const invoiceSettingsContextValue = useMemo(
    () => ({
      setInvoiceSettingsReferenceElement,
      invoiceSettingsReferenceElement,
    }),
    [setInvoiceSettingsReferenceElement, invoiceSettingsReferenceElement],
  );
  const handleTabClick = (tabIndex: number) => {
    if (tabIndex === activeTab) return;
    setActiveTab(tabIndex);
    if (tabIndex === TAB_INDEX.UNPAID) resetUnpaidTab();
    if (tabIndex === TAB_INDEX.HISTORY) resetHistoryTab();
  };

  return (
    <InvoiceSettingsContext.Provider value={invoiceSettingsContextValue}>
      <div className='sticky top-0 z-[30] bg-bw02'>
        <PageHeader title={t('INVOICE_HUB.TITLE')} showBackButton={false} />
        <div className='flex justify-between border-b-[1px] border-b-bw04 items-end'>
          <LiliTabs
            activeIndex={activeTab}
            onTabIndexChanged={(idx) => {
              handleTabClick(idx);
            }}
            tabs={[
              { title: t('INVOICE_HUB.UNPAID'), content: '', testId: 'webapp__invoice-hub__tab-unpaid' },
              { title: t('INVOICE_HUB.HISTORY'), content: '', testId: 'webapp__invoice-hub__tab-history' },
            ]}
          />
          <div className='flex mb-[6px]'>
            <InvoiceSettings shouldDisable={invoicesPermission !== FeatureAccess.READ_WRITE} />
            <LiliButton
              id='createNewInvoice'
              disabled={invoicesPermission !== FeatureAccess.READ_WRITE}
              testId='createNewInvoice'
              onClick={() => navigate('/invoice/create')}
              title={t('INVOICE_HUB.BUTTON_CREATE')}
              icon={<PlusSVG />}
              className='h-[40px] w-[196px]'
            />
          </div>
        </div>
      </div>
      <div className='mt-[30px]'>
        <InvoiceActionsContext.Provider value={invoiceActionsContextValue}>
          {activeTab === TAB_INDEX.UNPAID && <UnpaidInvoices />}
          {activeTab === TAB_INDEX.HISTORY && <HistoryInvoices />}
          <InvoiceHubModals />
        </InvoiceActionsContext.Provider>
      </div>
    </InvoiceSettingsContext.Provider>
  );
};
